import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Loader2, 
  Settings, 
  Edit,
  Power,
  Building,
  Calendar,
  DollarSign,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAM_TYPES = [
  { id: 'snap_et', name: 'SNAP E&T' },
  { id: 'housing_services', name: 'Housing Services' },
  { id: 'food_assistance', name: 'Food Assistance' },
  { id: 'recovery_services', name: 'Recovery Services' },
  { id: 'transportation', name: 'Transportation' },
  { id: 'crisis_services', name: 'Crisis Services' },
  { id: 'employment_training', name: 'Employment Training' },
  { id: 'mental_health', name: 'Mental Health Services' },
  { id: 'childcare', name: 'Childcare Assistance' },
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const ContractManagementPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    state: '',
    program_types: [],
    funding_source: '',
    contract_identifier: '',
    effective_start_date: '',
    effective_end_date: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchContracts();
  }, [includeInactive]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contracts?include_inactive=${includeInactive}`);
      setContracts(response.data);
    } catch (error) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      state: '',
      program_types: [],
      funding_source: '',
      contract_identifier: '',
      effective_start_date: '',
      effective_end_date: '',
      description: '',
      is_active: true
    });
  };

  const handleCreateContract = async () => {
    if (!formData.name || !formData.state || formData.program_types.length === 0 || !formData.funding_source || !formData.effective_start_date || !formData.effective_end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/contracts`, formData);
      toast.success('Contract created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateContract = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contracts/${selectedContract.id}`, formData);
      toast.success('Contract updated successfully');
      setShowEditDialog(false);
      setSelectedContract(null);
      resetForm();
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contract');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (contract) => {
    try {
      await axios.post(`${API}/contracts/${contract.id}/toggle-status`);
      toast.success(`Contract ${contract.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle contract status');
    }
  };

  const openEditDialog = (contract) => {
    setSelectedContract(contract);
    setFormData({
      name: contract.name,
      state: contract.state,
      program_types: contract.program_types || [],
      funding_source: contract.funding_source,
      contract_identifier: contract.contract_identifier || '',
      effective_start_date: contract.effective_start_date || '',
      effective_end_date: contract.effective_end_date || '',
      description: contract.description || '',
      is_active: contract.is_active !== false
    });
    setShowEditDialog(true);
  };

  const handleProgramTypeToggle = (programId) => {
    setFormData(prev => ({
      ...prev,
      program_types: prev.program_types.includes(programId)
        ? prev.program_types.filter(p => p !== programId)
        : [...prev.program_types, programId]
    }));
  };

  // Form field change handlers to prevent focus loss
  const handleNameChange = (e) => setFormData(prev => ({ ...prev, name: e.target.value }));
  const handleStateChange = (val) => setFormData(prev => ({ ...prev, state: val }));
  const handleFundingSourceChange = (e) => setFormData(prev => ({ ...prev, funding_source: e.target.value }));
  const handleStartDateChange = (e) => setFormData(prev => ({ ...prev, effective_start_date: e.target.value }));
  const handleEndDateChange = (e) => setFormData(prev => ({ ...prev, effective_end_date: e.target.value }));
  const handleContractIdChange = (e) => setFormData(prev => ({ ...prev, contract_identifier: e.target.value }));
  const handleDescriptionChange = (e) => setFormData(prev => ({ ...prev, description: e.target.value }));
  const handleIsActiveChange = (checked) => setFormData(prev => ({ ...prev, is_active: checked }));

  return (
    <div className="space-y-6" data-testid="contract-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="w-8 h-8 text-primary" />
            Contract Configuration
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage contracts, funding streams, and state agreements without code changes
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={includeInactive}
              onCheckedChange={setIncludeInactive}
            />
            <Label htmlFor="show-inactive" className="text-sm">Show inactive</Label>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                Add Contract
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Contract</DialogTitle>
                <DialogDescription>
                  Add a new contract or funding agreement. Changes take effect immediately.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label>Contract Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="e.g., Ohio SNAP E&T FFY25"
                    data-testid="contract-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Select value={formData.state} onValueChange={handleStateChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Funding Source *</Label>
                    <Input
                      value={formData.funding_source}
                      onChange={handleFundingSourceChange}
                      placeholder="e.g., USDA SNAP"
                      data-testid="contract-funding-source-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Program Types * (select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 border rounded-md">
                    {PROGRAM_TYPES.map(pt => (
                      <div key={pt.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`create-pt-${pt.id}`}
                          checked={formData.program_types.includes(pt.id)}
                          onChange={() => handleProgramTypeToggle(pt.id)}
                          className="w-4 h-4"
                        />
                        <label htmlFor={`create-pt-${pt.id}`} className="text-sm">{pt.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Effective Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.effective_start_date}
                      onChange={handleStartDateChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Effective End Date *</Label>
                    <Input
                      type="date"
                      value={formData.effective_end_date}
                      onChange={handleEndDateChange}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Contract Identifier (optional)</Label>
                  <Input
                    value={formData.contract_identifier}
                    onChange={handleContractIdChange}
                    placeholder="Internal reference number"
                    data-testid="contract-identifier-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder="Contract description..."
                    rows={2}
                    data-testid="contract-description-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateContract} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Contract
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Contracts List */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg">Active Contracts</CardTitle>
          <CardDescription>
            {contracts.length} contracts configured. These contracts are available for enrollment and case creation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : contracts.length > 0 ? (
            <div className="space-y-4">
              {contracts.map((contract) => (
                <div
                  key={contract.id}
                  className={`p-4 rounded-lg border ${contract.is_active !== false ? 'border-border' : 'border-red-200 bg-red-50/50 dark:bg-red-950/20'}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{contract.name}</h3>
                        <Badge className={contract.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {contract.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {contract.state}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {contract.funding_source}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {contract.effective_start_date} to {contract.effective_end_date}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {contract.program_types?.map(pt => (
                          <Badge key={pt} variant="secondary" className="text-xs">
                            {PROGRAM_TYPES.find(p => p.id === pt)?.name || pt}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(contract)}>
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant={contract.is_active !== false ? "destructive" : "default"} 
                        size="sm"
                        onClick={() => handleToggleStatus(contract)}
                      >
                        <Power className="w-4 h-4 mr-1" />
                        {contract.is_active !== false ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No contracts configured</p>
              <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Contract
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contract</DialogTitle>
            <DialogDescription>
              Update contract details. Changes take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Contract Name *</Label>
              <Input
                value={formData.name}
                onChange={handleNameChange}
                placeholder="e.g., Ohio SNAP E&T FFY25"
                data-testid="edit-contract-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>State *</Label>
                <Select value={formData.state} onValueChange={handleStateChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Funding Source *</Label>
                <Input
                  value={formData.funding_source}
                  onChange={handleFundingSourceChange}
                  placeholder="e.g., USDA SNAP"
                  data-testid="edit-contract-funding-source-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Program Types * (select all that apply)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 border rounded-md">
                {PROGRAM_TYPES.map(pt => (
                  <div key={pt.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`edit-pt-${pt.id}`}
                      checked={formData.program_types.includes(pt.id)}
                      onChange={() => handleProgramTypeToggle(pt.id)}
                      className="w-4 h-4"
                    />
                    <label htmlFor={`edit-pt-${pt.id}`} className="text-sm">{pt.name}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective Start Date *</Label>
                <Input
                  type="date"
                  value={formData.effective_start_date}
                  onChange={handleStartDateChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Effective End Date *</Label>
                <Input
                  type="date"
                  value={formData.effective_end_date}
                  onChange={handleEndDateChange}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Contract Identifier (optional)</Label>
              <Input
                value={formData.contract_identifier}
                onChange={handleContractIdChange}
                placeholder="Internal reference number"
                data-testid="edit-contract-identifier-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder="Contract description..."
                rows={2}
                data-testid="edit-contract-description-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-md">
              <div>
                <Label>Contract Status</Label>
                <p className="text-xs text-muted-foreground">Inactive contracts won&apos;t appear in enrollment/case dropdowns</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={handleIsActiveChange}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateContract} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractManagementPage;
